import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileArrowUp, FileXls, Trash, Check, SpinnerGap, Gear, Download } from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadBase64File } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TemplateCard({ type, label, template, inputRef, uploading, onUpload, onDelete, onTest }) {
  return (
    <div className="border border-zinc-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileXls size={20} className="text-emerald-600" />
          <span className="font-medium text-sm">{label}</span>
        </div>
        {template && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-rose-500 hover:text-rose-700 text-xs"
            onClick={() => onDelete(type)}
          >
            <Trash size={12} className="mr-1" /> Remover
          </Button>
        )}
      </div>

      {template ? (
        <div className="space-y-2">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Check size={14} className="text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700">Modelo configurado</span>
            </div>
            <p className="text-xs text-emerald-600">{template.file_name}</p>
            {template.structure?.colunas && (
              <p className="text-[10px] text-emerald-500 mt-1">
                Colunas detectadas: {template.structure.colunas.map((c) => c.nome).join(", ")}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7 flex-1"
              onClick={() => inputRef.current?.click()}
            >
              <FileArrowUp size={12} className="mr-1" /> Trocar modelo
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7 flex-1"
              onClick={() => onTest(type)}
            >
              <Download size={12} className="mr-1" /> Testar exportacao
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-center py-4">
          {uploading === type ? (
            <div className="flex flex-col items-center gap-2">
              <SpinnerGap size={24} className="text-zinc-400 animate-spin" />
              <p className="text-xs text-zinc-500">IA analisando estrutura do modelo...</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-zinc-500 mb-3">
                Envie um modelo Excel para que o Sous-Chef exporte seus dados nesse formato
              </p>
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => inputRef.current?.click()}
              >
                <FileArrowUp size={14} className="mr-1" /> Enviar modelo Excel
              </Button>
            </>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept=".xlsx,.xls,.xlsm,.csv"
        onChange={(e) => {
          onUpload(e.target.files?.[0], type);
          e.target.value = "";
        }}
      />
    </div>
  );
}

export default function ExportTemplateConfig({ open, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const fichaRef = useRef(null);
  const listaRef = useRef(null);

  useEffect(() => {
    if (open) loadTemplates();
  }, [open]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/export-templates`);
      setTemplates(data);
    } catch { /* empty */ }
    setLoading(false);
  };

  const handleUpload = async (file, type) => {
    if (!file) return;
    setUploading(type);

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await axios.post(`${API}/export-templates`, {
          template_type: type,
          file_base64: reader.result,
          file_name: file.name,
        });
        toast.success(`Modelo "${file.name}" salvo! IA analisou a estrutura automaticamente.`);
        loadTemplates();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erro ao salvar modelo");
      }
      setUploading(null);
    };
    reader.readAsDataURL(file);
  };

  const handleDelete = async (type) => {
    try {
      await axios.delete(`${API}/export-templates/${type}`);
      toast.success("Modelo removido");
      loadTemplates();
    } catch {
      toast.error("Erro ao remover");
    }
  };

  const handleTestExport = async (type) => {
    try {
      const { data } = await axios.post(`${API}/export/generate`, { type });
      const mime = data.is_zip
        ? "application/zip"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      downloadBase64File(data.file_base64, data.file_name, mime);
      toast.success(data.is_zip ? `${data.file_count} fichas exportadas em ZIP!` : "Exportação gerada!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao exportar");
    }
  };

  const fichaTemplate = templates.find((t) => t.template_type === "ficha_tecnica");
  const listaTemplate = templates.find((t) => t.template_type === "lista_compras");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <div className={open ? "fixed inset-0 z-[95] bg-black/50" : "hidden"} onClick={onClose} />
      <DialogContent className="sm:max-w-[500px] z-[100] max-h-[90vh] overflow-y-auto fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]" data-testid="export-template-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold flex items-center gap-2">
            <Gear size={20} />
            Modelos de Exportacao
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-zinc-500 -mt-2">
          Configure seus modelos de Excel personalizados. O Sous-Chef vai exportar fichas tecnicas e listas de compras no seu formato quando voce pedir.
        </p>

        {loading ? (
          <div className="py-8 text-center">
            <SpinnerGap size={24} className="text-zinc-300 animate-spin mx-auto" />
          </div>
        ) : (
          <div className="space-y-3">
            <TemplateCard
              type="ficha_tecnica"
              label="Modelo de Ficha Tecnica"
              template={fichaTemplate}
              inputRef={fichaRef}
              uploading={uploading}
              onUpload={handleUpload}
              onDelete={handleDelete}
              onTest={handleTestExport}
            />
            <TemplateCard
              type="lista_compras"
              label="Modelo de Lista de Compras"
              template={listaTemplate}
              inputRef={listaRef}
              uploading={uploading}
              onUpload={handleUpload}
              onDelete={handleDelete}
              onTest={handleTestExport}
            />
          </div>
        )}

        <div className="bg-zinc-50 rounded-lg p-3 mt-1">
          <p className="text-[11px] text-zinc-500">
            <strong>Como usar:</strong> Apos configurar, basta pedir ao Sous-Chef:
          </p>
          <p className="text-[11px] text-zinc-600 mt-1 italic">
            {`"Exporte minha ficha de Bolo de Chocolate no meu modelo"`}
          </p>
          <p className="text-[11px] text-zinc-600 italic">
            {`"Gere a lista de compras no formato da minha planilha"`}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
